import { DatePicker, Input, message, Modal, Progress } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useContext, useEffect, useState } from 'react';
import { Agent } from '../../types';
import { ClientConfig } from '../../types/config';
import { UserContext } from '../../UserContext';
import { HWButton } from '../shared/StyledButton';

dayjs.extend(utc);
dayjs.extend(timezone);

const { TextArea } = Input;

interface SendMessageModalProps {
  visible: boolean;
  onClose: () => void;
  selectedAgents: Agent[];
  apiUrl: string;
  clientConfig: ClientConfig | null;
}

interface AgentInfo {
  nickname?: string;
  agents_nickname?: string;
  assistant_name?: string;
}

export function SendMessageModal({
  visible,
  onClose,
  selectedAgents,
  apiUrl,
  clientConfig,
}: SendMessageModalProps) {
  const user = useContext(UserContext);
  const [messageText, setMessageText] = useState('');
  const [localScheduledDate, setLocalScheduledDate] = useState<Dayjs | null>(
    null,
  );
  const [messageError, setMessageError] = useState('');
  const [dateError, setDateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentAgent, setCurrentAgent] = useState<string>('');

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      setMessageText('');
      setLocalScheduledDate(null);
      setMessageError('');
      setDateError('');
    }
  }, [visible]);

  const handleCreate = async () => {
    // Clear previous errors
    setMessageError('');
    setDateError('');

    // Simple validation
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage) {
      setMessageError('Please enter a message');
      return;
    }

    if (!localScheduledDate) {
      setDateError('Please select a date and time');
      return;
    }

    if (!user?.accessToken) {
      void message.error('No access token available');
      return;
    }

    if (selectedAgents.length === 0) {
      void message.error('No agents selected');
      return;
    }

    try {
      setCreating(true);
      setProgress(0);
      setCurrentAgent('');

      const totalAgents = selectedAgents.length;
      let successCount = 0;
      let errorCount = 0;

      // Loop through each selected agent
      for (let i = 0; i < selectedAgents.length; i++) {
        const agent = selectedAgents[i];
        setCurrentAgent(`${agent.first_name} ${agent.last_name}`);
        setProgress((i / totalAgents) * 100);

        try {
          // Fetch agent info to get nickname and assistant_name
          const agentInfoResponse = await fetch(
            `${apiUrl}/api/agent/info/${agent.id}`,
            {
              headers: {
                Authorization: `Bearer ${user.accessToken}`,
              },
            },
          );

          if (!agentInfoResponse.ok) {
            throw new Error(
              `Failed to fetch agent info: ${agentInfoResponse.status}`,
            );
          }

          const agentData = await agentInfoResponse.json();
          const personalization: AgentInfo = agentData.personalization || {};

          // Get agent nickname and AI name
          // Check both field names for compatibility
          const agentNickname =
            personalization.agents_nickname ||
            personalization.nickname ||
            agent.first_name;
          const aiName =
            personalization.assistant_name ||
            clientConfig?.default_assistant_name ||
            'Alex';

          // Render the message template
          let renderedMessage = trimmedMessage;
          renderedMessage = renderedMessage.replace(
            /\{Agent Name\}/g,
            agentNickname,
          );
          renderedMessage = renderedMessage.replace(/\{AI Name\}/g, aiName);

          // Format the date/time as a string without timezone information
          // The API will assume the date/time is in the agent's local timezone
          const localDateString = localScheduledDate.format(
            'YYYY-MM-DD HH:mm:ss',
          );

          // Create the reminder using the same API as Reminders.tsx
          const payload = {
            hardcoded_message: renderedMessage,
            date: localDateString,
          };

          const response = await fetch(
            `${apiUrl}/api/tasks/${agent.id}/post_task`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.accessToken}`,
              },
              body: JSON.stringify(payload),
            },
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error ||
                `Failed to create reminder: ${response.status}`,
            );
          }

          successCount++;
        } catch (error) {
          console.error(
            `Error creating reminder for ${agent.first_name} ${agent.last_name}:`,
            error,
          );
          errorCount++;
          // Continue with next agent even if one fails
        }
      }

      setProgress(100);
      setCurrentAgent('');

      // Show summary message
      if (successCount === totalAgents) {
        void message.success(
          `Successfully created ${successCount} reminder${successCount > 1 ? 's' : ''}`,
        );
        setMessageText('');
        setLocalScheduledDate(null);
        onClose();
      } else if (successCount > 0) {
        void message.warning(
          `Created ${successCount} reminder${successCount > 1 ? 's' : ''}, ${errorCount} failed`,
        );
      } else {
        void message.error(
          `Failed to create reminders. ${errorCount} error${errorCount > 1 ? 's' : ''}`,
        );
      }
    } catch (error) {
      console.error('Error creating reminders:', error);
      void message.error('Failed to create reminders');
    } finally {
      setCreating(false);
      setProgress(0);
      setCurrentAgent('');
    }
  };

  const handleCancel = () => {
    if (!creating) {
      setMessageText('');
      setLocalScheduledDate(null);
      setMessageError('');
      setDateError('');
      onClose();
    }
  };

  return (
    <Modal
      footer={null}
      onCancel={handleCancel}
      open={visible}
      title="Send Message"
      width={600}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 8 }}>
            <span style={{ color: 'red' }}>*</span> Message
          </label>
          <TextArea
            onChange={e => {
              setMessageText(e.target.value);
              if (messageError && e.target.value.trim()) {
                setMessageError('');
              }
            }}
            placeholder="Enter your message. Use {Agent Name} and {AI Name} as placeholders."
            rows={6}
            value={messageText}
          />
          {messageError && (
            <div style={{ color: 'red', marginTop: 4, fontSize: 14 }}>
              {messageError}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Template variables: {'{Agent Name}'} and {'{AI Name}'}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 8 }}>
            <span style={{ color: 'red' }}>*</span> Date & Time
          </label>
          <DatePicker
            disabledDate={current => {
              // Disable past dates
              return current && current < dayjs().startOf('day');
            }}
            format="YYYY-MM-DD HH:mm:ss"
            onChange={value => {
              setLocalScheduledDate(value);
              if (dateError && value) {
                setDateError('');
              }
            }}
            placeholder="Select date and time"
            showTime
            style={{ width: '100%' }}
            value={localScheduledDate}
          />
          {dateError && (
            <div style={{ color: 'red', marginTop: 4, fontSize: 14 }}>
              {dateError}
            </div>
          )}
        </div>

        {creating && (
          <div style={{ marginBottom: 16 }}>
            <Progress
              format={percent => `${Math.round(percent ?? 0)}%`}
              percent={progress}
              status="active"
            />
            {currentAgent && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                Creating reminder for: {currentAgent}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <HWButton disabled={creating} onClick={handleCancel}>
            Cancel
          </HWButton>
          <HWButton
            disabled={creating}
            loading={creating}
            onClick={() => {
              void handleCreate();
            }}
            type="primary"
          >
            Create
          </HWButton>
        </div>
      </div>
    </Modal>
  );
}
